import { Helmet } from "react-helmet-async";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import TiptapLink from "@tiptap/extension-link";
import { Bold, Italic, Heading1, Heading2, List, ListOrdered, ImageIcon, Link as LinkIcon, ArrowLeft, Upload, Sparkles } from "lucide-react";
import { toast } from "sonner";
import type { Session } from "@supabase/supabase-js";
import SeoAnalyzer from "@/components/SeoAnalyzer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const BlogEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [status, setStatus] = useState<"draft" | "published" | "scheduled">("draft");
  const [scheduledFor, setScheduledFor] = useState<string>(""); // datetime-local string
  const [featuredImage, setFeaturedImage] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [focusKeyword, setFocusKeyword] = useState("");
  const [contentHtml, setContentHtml] = useState("");

  // Alt-text dialog state for inline image uploads
  const [altDialogOpen, setAltDialogOpen] = useState(false);
  const [pendingImageUrl, setPendingImageUrl] = useState("");
  const [pendingImageFilename, setPendingImageFilename] = useState("");
  const [altText, setAltText] = useState("");

  const cleanFilenameForAlt = (name: string) => {
    const base = name.replace(/\.[^.]+$/, "");
    return base
      .replace(/[-_]+/g, " ")
      .replace(/\b\d{6,}\b/g, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const getNearestHeadingText = () => {
    if (!editor) return "";
    // Find heading just before current cursor position
    const { from } = editor.state.selection;
    let nearest = "";
    editor.state.doc.descendants((node, pos) => {
      if (pos > from) return false;
      if (node.type.name === "heading") {
        nearest = node.textContent;
      }
      return true;
    });
    if (nearest) return nearest;
    // Fallback: first heading in the document
    editor.state.doc.descendants((node) => {
      if (!nearest && node.type.name === "heading") {
        nearest = node.textContent;
      }
      return true;
    });
    return nearest;
  };

  const suggestAltText = () => {
    const fromFile = cleanFilenameForAlt(pendingImageFilename);
    const heading = getNearestHeadingText();
    const parts = [fromFile, heading].filter(Boolean);
    const unique = Array.from(new Set(parts.map((p) => p.toLowerCase())))
      .map((low) => parts.find((p) => p.toLowerCase() === low) as string);
    const suggestion = unique.join(" – ") || title || "";
    if (!suggestion) {
      toast.info("Add a heading or a descriptive filename to get a suggestion.");
      return;
    }
    setAltText(suggestion.charAt(0).toUpperCase() + suggestion.slice(1));
  };

  const confirmInsertImage = () => {
    if (!pendingImageUrl) return;
    editor?.chain().focus().setImage({ src: pendingImageUrl, alt: altText.trim() }).run();
    setAltDialogOpen(false);
    setPendingImageUrl("");
    setPendingImageFilename("");
    setAltText("");
  };


  const { data: categories } = useQuery({
    queryKey: ["all-categories"],
    queryFn: async () => {
      const { data } = await supabase.from("blog_categories").select("*").order("name");
      return data || [];
    },
  });

  const { data: postCategories } = useQuery({
    queryKey: ["post-categories", id],
    enabled: isEditing && !!id,
    queryFn: async () => {
      const { data } = await supabase
        .from("blog_post_categories")
        .select("category_id")
        .eq("post_id", id!);
      return data?.map((d: any) => d.category_id) || [];
    },
  });

  useEffect(() => {
    if (postCategories) setSelectedCategories(postCategories);
  }, [postCategories]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      TiptapLink.configure({ openOnClick: false }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none min-h-[300px] p-4 focus:outline-none prose-headings:text-foreground prose-p:text-foreground/80 prose-a:text-primary",
      },
    },
    onUpdate: ({ editor }) => {
      setContentHtml(editor.getHTML());
    },
  });

  const { data: existingPost } = useQuery({
    queryKey: ["edit-post", id],
    enabled: isEditing && !!session,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (existingPost) {
      setTitle(existingPost.title);
      setSlug(existingPost.slug);
      setExcerpt(existingPost.excerpt || "");
      const rawStatus = existingPost.status as string;
      const publishedAt = existingPost.published_at ? new Date(existingPost.published_at) : null;
      if (rawStatus === "scheduled" || (rawStatus === "published" && publishedAt && publishedAt > new Date())) {
        setStatus("scheduled");
        // Format for datetime-local input (local time, no seconds)
        if (publishedAt) {
          const tzOffset = publishedAt.getTimezoneOffset() * 60000;
          setScheduledFor(new Date(publishedAt.getTime() - tzOffset).toISOString().slice(0, 16));
        }
      } else {
        setStatus(rawStatus as "draft" | "published");
      }
      setFeaturedImage(existingPost.featured_image || "");
      setPdfUrl((existingPost as any).pdf_url || "");
      setFocusKeyword((existingPost as any).focus_keyword || "");
      editor?.commands.setContent(existingPost.content || "");
      setContentHtml(existingPost.content || "");
    }
  }, [existingPost, editor]);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!isEditing) {
      setSlug(generateSlug(value));
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const content = editor?.getHTML() || "";

      let resolvedStatus: string = status;
      let resolvedPublishedAt: string | null = null;

      if (status === "scheduled") {
        if (!scheduledFor) {
          throw new Error("Please pick a date and time to schedule this post.");
        }
        const scheduledDate = new Date(scheduledFor);
        if (isNaN(scheduledDate.getTime())) {
          throw new Error("Invalid scheduled date.");
        }
        if (scheduledDate <= new Date()) {
          resolvedStatus = "published";
          resolvedPublishedAt = new Date().toISOString();
        } else {
          // Store as "published" with a future timestamp; public pages gate on the timestamp.
          resolvedStatus = "published";
          resolvedPublishedAt = scheduledDate.toISOString();
        }
      } else if (status === "published") {
        resolvedPublishedAt =
          (existingPost as any)?.published_at || new Date().toISOString();
      }

      const postData: any = {
        title,
        slug,
        content,
        excerpt: excerpt || null,
        featured_image: featuredImage || null,
        pdf_url: pdfUrl || null,
        focus_keyword: focusKeyword || null,
        status: resolvedStatus,
        published_at: resolvedPublishedAt,
        author_id: session?.user?.id,
      };

      let postId = id;

      if (isEditing) {
        const { error } = await supabase
          .from("blog_posts")
          .update(postData)
          .eq("id", id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("blog_posts")
          .insert(postData)
          .select("id")
          .single();
        if (error) throw error;
        postId = data.id;
      }

      // Update categories
      if (postId) {
        await supabase.from("blog_post_categories").delete().eq("post_id", postId);
        if (selectedCategories.length > 0) {
          await supabase.from("blog_post_categories").insert(
            selectedCategories.map((catId) => ({ post_id: postId!, category_id: catId }))
          );
        }
      }
    },
    onSuccess: () => {
      toast.success(isEditing ? "Post updated!" : "Post created!");
      navigate("/admin/blog");
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  // Allowed upload types — must match the server-side bucket allowlist
  const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
  const ALLOWED_IMAGE_EXTS = ["png", "jpg", "jpeg", "webp", "gif"];
  const ALLOWED_PDF_TYPES = ["application/pdf"];

  const validateFile = (
    file: File,
    allowedMimes: string[],
    allowedExts: string[]
  ): string | null => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!allowedExts.includes(ext)) {
      return `File extension .${ext} is not allowed.`;
    }
    if (file.type && !allowedMimes.includes(file.type)) {
      return `File type ${file.type} is not allowed.`;
    }
    if (file.size > 10 * 1024 * 1024) {
      return "File is larger than 10 MB.";
    }
    return null;
  };

  const handleFeaturedImageUpload = useCallback(async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ALLOWED_IMAGE_TYPES.join(",");
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const validationError = validateFile(file, ALLOWED_IMAGE_TYPES, ALLOWED_IMAGE_EXTS);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      const fileExt = (file.name.split(".").pop() || "").toLowerCase();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `featured/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("blog-images")
        .upload(filePath, file, { contentType: file.type, upsert: false });

      if (uploadError) {
        toast.error("Failed to upload image");
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from("blog-images")
        .getPublicUrl(filePath);

      setFeaturedImage(publicUrl);
      toast.success("Featured image uploaded!");
    };
    input.click();
  }, []);

  const handleImageUpload = useCallback(async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ALLOWED_IMAGE_TYPES.join(",");
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const validationError = validateFile(file, ALLOWED_IMAGE_TYPES, ALLOWED_IMAGE_EXTS);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      const fileExt = (file.name.split(".").pop() || "").toLowerCase();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `posts/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("blog-images")
        .upload(filePath, file, { contentType: file.type, upsert: false });

      if (uploadError) {
        toast.error("Failed to upload image");
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from("blog-images")
        .getPublicUrl(filePath);

      const altSuggestion = cleanFilenameForAlt(file.name);
      setPendingImageUrl(publicUrl);
      setPendingImageFilename(file.name);
      setAltText(altSuggestion);
      setAltDialogOpen(true);
    };
    input.click();
  }, [editor]);

  const handlePdfUpload = useCallback(async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const validationError = validateFile(file, ALLOWED_PDF_TYPES, ["pdf"]);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const fileName = `${Date.now()}-${safeName}`;
      const filePath = `pdfs/${fileName}`;


      const { error: uploadError } = await supabase.storage
        .from("blog-images")
        .upload(filePath, file, { contentType: "application/pdf", upsert: false });

      if (uploadError) {
        toast.error("Failed to upload PDF");
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from("blog-images")
        .getPublicUrl(filePath);

      setPdfUrl(publicUrl);
      toast.success("PDF uploaded!");
    };
    input.click();
  }, []);

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen pt-24 flex items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </main>
        <Footer />
      </>
    );
  }

  if (!session) {
    navigate("/admin/blog");
    return null;
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Blog Editor | Cetasol</title>
      </Helmet>
      <Header />
      <main className="min-h-screen pt-24 pb-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <button
            onClick={() => navigate("/admin/blog")}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft size={16} />
            Back to admin
          </button>

          <h1 className="text-3xl font-bold mb-8">
            {isEditing ? "Edit Post" : "New Post"}
          </h1>

          <div className="space-y-6">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Post title"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="post-slug"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="excerpt">Excerpt</Label>
              <Input
                id="excerpt"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder="Brief description of the post"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="focus_keyword">Focus keywords (SEO)</Label>
              <Input
                id="focus_keyword"
                value={focusKeyword}
                onChange={(e) => setFocusKeyword(e.target.value)}
                placeholder="e.g. virtual fuel sensing, fleet optimization"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                One main phrase, or several separated by commas. Each keyword is scored separately below.
              </p>
            </div>

            <SeoAnalyzer
              title={title}
              slug={slug}
              excerpt={excerpt}
              content={contentHtml}
              focusKeyword={focusKeyword}
              featuredImage={featuredImage}
            />

            <div>
              <Label htmlFor="featured_image">Featured Image</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="featured_image"
                  value={featuredImage}
                  onChange={(e) => setFeaturedImage(e.target.value)}
                  placeholder="Image URL (or upload below)"
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={handleFeaturedImageUpload} className="gap-2">
                  <Upload size={16} />
                  Upload Image
                </Button>
              </div>
              {featuredImage && (
                <img src={featuredImage} alt="Featured preview" className="mt-2 max-h-40 rounded border border-border" />
              )}
            </div>

            {/* PDF Upload */}
            <div>
              <Label>PDF File (for White Papers)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={pdfUrl}
                  onChange={(e) => setPdfUrl(e.target.value)}
                  placeholder="PDF URL (or upload below)"
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={handlePdfUpload} className="gap-2">
                  <Upload size={16} />
                  Upload PDF
                </Button>
              </div>
              {pdfUrl && (
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary mt-1 inline-block hover:underline">
                  View uploaded PDF
                </a>
              )}
            </div>

            {/* Categories */}
            <div>
              <Label>Categories</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {categories?.map((cat: any) => (
                  <Button
                    key={cat.id}
                    type="button"
                    variant={selectedCategories.includes(cat.id) ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setSelectedCategories((prev) =>
                        prev.includes(cat.id)
                          ? prev.filter((c) => c !== cat.id)
                          : [...prev, cat.id]
                      );
                    }}
                  >
                    {cat.name}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label>Status</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                <Button
                  type="button"
                  variant={status === "draft" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatus("draft")}
                >
                  Draft
                </Button>
                <Button
                  type="button"
                  variant={status === "published" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatus("published")}
                >
                  Publish now
                </Button>
                <Button
                  type="button"
                  variant={status === "scheduled" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setStatus("scheduled");
                    if (!scheduledFor) {
                      // Default to one hour from now in local time
                      const d = new Date(Date.now() + 60 * 60 * 1000);
                      const tzOffset = d.getTimezoneOffset() * 60000;
                      setScheduledFor(new Date(d.getTime() - tzOffset).toISOString().slice(0, 16));
                    }
                  }}
                >
                  Schedule
                </Button>
              </div>
              {status === "scheduled" && (
                <div className="mt-3">
                  <Label htmlFor="scheduled_for" className="text-sm">
                    Publish at (your local time)
                  </Label>
                  <Input
                    id="scheduled_for"
                    type="datetime-local"
                    value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    className="mt-1 max-w-xs"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    The post stays hidden from visitors until this time.
                  </p>
                </div>
              )}
            </div>

            {/* Editor toolbar */}
            <div>
              <Label>Content</Label>
              <div className="mt-1 border border-border rounded-lg overflow-hidden bg-card">
                <div className="flex flex-wrap gap-1 p-2 border-b border-border bg-secondary/50">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                  >
                    <Bold size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                  >
                    <Italic size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                  >
                    <Heading1 size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                  >
                    <Heading2 size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                  >
                    <List size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                  >
                    <ListOrdered size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      const url = prompt("Enter link URL");
                      if (url) {
                        editor?.chain().focus().setLink({ href: url }).run();
                      }
                    }}
                  >
                    <LinkIcon size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleImageUpload}
                  >
                    <ImageIcon size={16} />
                  </Button>
                </div>
                <EditorContent editor={editor} />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !title}
              >
                {saveMutation.isPending
                  ? "Saving..."
                  : isEditing
                  ? "Update Post"
                  : "Create Post"}
              </Button>
              <Button variant="outline" onClick={() => navigate("/admin/blog")}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      <Dialog open={altDialogOpen} onOpenChange={setAltDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add alt text for this image</DialogTitle>
            <DialogDescription>
              Alt text is read aloud by screen readers and used by search engines. Describe what the image shows.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {pendingImageUrl && (
              <img
                src={pendingImageUrl}
                alt=""
                className="max-h-48 rounded border border-border mx-auto"
              />
            )}
            <Input
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="e.g. Vessel sailing at sunset with fuel sensor overlay"
              autoFocus
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={suggestAltText}
              className="gap-2"
            >
              <Sparkles size={14} />
              Suggest alt text
            </Button>
            <p className="text-xs text-muted-foreground">
              Suggestions combine the filename and the nearest heading in your post.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAltDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmInsertImage}>Insert image</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>

  );
};

export default BlogEditor;
